import type {
  ChangelogData,
  ChangelogEntry,
  ChangelogVersion,
} from "./changelogTypes";

export type ChangelogVersionWithUnread = {
  version: string;
  date: string;
  entries: ChangelogEntry[];
};

export function getVersionsNewestFirst(
  data: ChangelogData
): ChangelogVersion[] {
  return [...data.versions].reverse();
}

export function allEntryIds(data: ChangelogData): string[] {
  return data.versions.flatMap((version) =>
    version.entries.map((entry) => entry.id)
  );
}

export function getAllEntries(data: ChangelogData): ChangelogEntry[] {
  return data.versions.flatMap((version) => version.entries);
}

export function getUnreadEntries(
  data: ChangelogData,
  seenEntryIds: string[]
): ChangelogEntry[] {
  const seen = new Set(seenEntryIds);
  return getAllEntries(data).filter((entry) => !seen.has(entry.id));
}

export function getUnreadCount(
  data: ChangelogData,
  seenEntryIds: string[]
): number {
  return getUnreadEntries(data, seenEntryIds).length;
}

/** Unread entries grouped by version, versions and entries newest-first. */
export function getUnreadVersionsNewestFirst(
  data: ChangelogData,
  seenEntryIds: string[]
): ChangelogVersionWithUnread[] {
  const seen = new Set(seenEntryIds);

  return getVersionsNewestFirst(data)
    .map((version) => ({
      version: version.version,
      date: version.date,
      // Entries are stored newest-first (see generate-changelog skill).
      entries: version.entries.filter((entry) => !seen.has(entry.id)),
    }))
    .filter((version) => version.entries.length > 0);
}

/** All versions with entries newest-first (full history). */
export function getAllVersionsNewestFirst(
  data: ChangelogData
): ChangelogVersionWithUnread[] {
  return getVersionsNewestFirst(data).map((version) => ({
    version: version.version,
    date: version.date,
    entries: version.entries,
  }));
}
